import React from "react";

/**
 * Iter 20.10 — Faksimile-Karten für die Demo-Slideshow.
 *
 * Alle Karten sind reine HTML/SVG — keine externen Bilder, keine echten
 * Kundendaten. Fiktive Namen, fiktive Zeiträume, aber realistisches Layout.
 * Ziel: der Interessent erkennt den Dokumenttyp auf einen Blick, ohne dass
 * wir echte Behörden-Faksimiles verwenden müssen.
 */

/* ─────────────────────────────────────────────────────────────────────────
 * SV-AUSZUG (Sozialversicherungsdatenauszug)
 * ─────────────────────────────────────────────────────────────────────── */
export function SVFaksimile({ rows, highlightIndex, personName = "Muster, Max" }) {
  return (
    <div
      className="border-2 border-ink-300 bg-white rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.06)] p-5 relative"
      data-testid="faksimile-sv"
    >
      {/* Kopf */}
      <div className="border-b-2 border-ink-800 pb-3 mb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-warm-500 mb-1">
              Republik Österreich
            </div>
            <div className="font-cabinet text-lg tracking-tight text-ink-900 font-medium leading-tight">
              Sozialversicherungsdatenauszug
            </div>
          </div>
          <div className="text-[9px] text-warm-500 text-right leading-tight">
            SVA-Nr.<br />1234 010589
          </div>
        </div>
        <div className="mt-2 text-[11px] text-warm-700">
          <strong className="text-ink-900">{personName}</strong> · Auszug per
          Stichtag 21. Juli 2026
        </div>
      </div>

      {/* Tabelle */}
      <table className="w-full text-[11px]" data-testid="faksimile-sv-table">
        <thead>
          <tr className="border-b border-ink-300">
            <th className="text-left py-1.5 pr-2 uppercase tracking-wider text-[9px] font-semibold text-warm-600">
              Zeitraum
            </th>
            <th className="text-left py-1.5 pr-2 uppercase tracking-wider text-[9px] font-semibold text-warm-600">
              Dienstgeber
            </th>
            <th className="text-left py-1.5 pr-2 uppercase tracking-wider text-[9px] font-semibold text-warm-600">
              Beschäftigungsart
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const isHl = idx === highlightIndex;
            return (
              <tr
                key={idx}
                className={[
                  "border-b border-ink-100",
                  isHl ? "bg-brand-50 relative" : "",
                ].join(" ")}
              >
                <td className="py-1.5 pr-2 text-ink-800 whitespace-nowrap font-mono text-[10px]">
                  {r.von} – {r.bis}
                </td>
                <td className="py-1.5 pr-2 text-ink-800">{r.arbeitgeber}</td>
                <td className="py-1.5 pr-2 text-warm-700">{r.art}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Callout */}
      {typeof highlightIndex === "number" && (
        <div
          className="absolute -right-2 top-0 translate-x-full hidden lg:flex items-center gap-2 pointer-events-none"
          style={{ top: `${115 + highlightIndex * 26}px` }}
          data-testid="faksimile-sv-callout"
        >
          <div className="h-px w-8 bg-brand-500" />
          <div className="bg-brand-500 text-white text-[10px] px-2 py-1 rounded-sm font-semibold whitespace-nowrap">
            Diese Zeile erkennt ET
          </div>
        </div>
      )}

      {/* Fuß */}
      <div className="mt-3 pt-2 border-t border-ink-200 text-[9px] text-warm-500 italic">
        Beispiel-Faksimile — fiktive Daten
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * LEBENSLAUF (CV)
 * ─────────────────────────────────────────────────────────────────────── */
export function CVFaksimile({ personName, subtitle, positions, education }) {
  return (
    <div
      className="border-2 border-ink-300 bg-white rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.06)] p-5"
      data-testid="faksimile-cv"
    >
      <div className="border-b border-ink-300 pb-2 mb-3">
        <div className="text-[9px] uppercase tracking-widest text-warm-500 mb-1">
          Lebenslauf
        </div>
        <div className="font-cabinet text-xl tracking-tight text-ink-900 font-medium leading-tight">
          {personName}
        </div>
        {subtitle && (
          <div className="mt-0.5 text-[11px] text-warm-600">{subtitle}</div>
        )}
      </div>

      <div className="mb-3">
        <div className="text-[9px] uppercase tracking-widest text-warm-500 mb-1.5 font-semibold">
          Berufserfahrung
        </div>
        <ul className="space-y-1.5 text-[11px]">
          {positions.map((p, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="font-mono text-[10px] text-warm-600 whitespace-nowrap min-w-[95px]">
                {p.zeitraum}
              </span>
              <span className="flex-1">
                <span className="text-ink-900 font-semibold">
                  {p.arbeitgeber}
                </span>{" "}
                — <span className="text-warm-700">{p.taetigkeit}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {education && education.length > 0 && (
        <div>
          <div className="text-[9px] uppercase tracking-widest text-warm-500 mb-1.5 font-semibold">
            Ausbildung
          </div>
          <ul className="space-y-1 text-[11px]">
            {education.map((e, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="font-mono text-[10px] text-warm-600 whitespace-nowrap min-w-[95px]">
                  {e.zeitraum}
                </span>
                <span className="flex-1">
                  <span className="text-ink-900 font-semibold">
                    {e.einrichtung}
                  </span>{" "}
                  — <span className="text-warm-700">{e.abschluss}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-ink-200 text-[9px] text-warm-500 italic">
        Beispiel-Lebenslauf — fiktive Daten
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * KV-AUSZUG (Zitat-Card)
 * ─────────────────────────────────────────────────────────────────────── */
export function KVFaksimile({ kvName, paragraph, quote, note }) {
  return (
    <div
      className="border-2 border-ink-300 bg-warm-50/60 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.06)] p-5 relative"
      data-testid="faksimile-kv"
    >
      <div className="flex items-start gap-3 mb-2">
        <div className="text-2xl font-cabinet tracking-tight text-brand-500 font-medium leading-none mt-0.5">
          §
        </div>
        <div className="flex-1">
          <div className="text-[9px] uppercase tracking-widest text-warm-500">
            Kollektivvertrag
          </div>
          <div className="font-cabinet text-base tracking-tight text-ink-900 font-medium leading-tight">
            {kvName}
          </div>
          <div className="text-[10px] text-warm-600 mt-0.5">{paragraph}</div>
        </div>
      </div>

      <blockquote className="border-l-2 border-brand-400 pl-3 text-[12px] italic text-ink-800 leading-relaxed">
        &laquo;{quote}&raquo;
      </blockquote>

      {note && (
        <div className="mt-3 text-[10px] text-warm-700 leading-relaxed">
          {note}
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-ink-200 text-[9px] text-warm-500 italic">
        Beispiel-Auszug — sinngemäß formuliert, kein wörtliches Zitat
      </div>
    </div>
  );
}
